import { BaseRepository } from '../BaseRepository';
import { BFF_ENDPOINTS } from '../../endpoints/BffEndpoints';
import type {
  AuthorizationData,
  AuthorizeResponse,
  BookingPaymentIntentData,
  ChangeCardPaymentIntentData,
  ChangePaymentMethodPayload,
  ChangePaymentMethodResponse,
  PayerData,
  PayerResponse,
  PaymentResponse,
  PaymentIntentResponse,
  SubscriptionPaymentIntentData,
} from '../../models/bff/Payment';
import type { PaymentAuthenticationProviderType, PaymentProviderType, TokenizationInitResponse } from '../../../models/payment/PaymentProvider';
import type { CreatePaymentAuthenticationPayload, PaymentAuthenticationResponse } from '../../models/bff/PaymentAuthentication';
import { BffAuthenticatedApiClient } from '../../clients/BffAuthenticatedApiClient';
import type { HttpResponse } from '../../http/HttpClient';
import { IdempotencyManager } from '../../IdempotencyManager';

import type { BillingPaymentResponse } from '../../models/bff/Billing';

const NAME = 'payment';

// Workflow polling retry configuration
const MAX_RETRY_ATTEMPTS = 10;
const INITIAL_RETRY_DELAY_MS = 500;
const RETRY_BACKOFF_BASE = 2;
const MAX_BACKOFF_EXPONENT = 5;
const HTTP_REQUEST_TIMEOUT = 408;

/**
 * Repository for payment-related operations.
 * Handles communication with the BFF for payment processing.
 */
export class PaymentRepository extends BaseRepository {
  static override readonly name = NAME;
  private readonly idempotencyManager = new IdempotencyManager();

  constructor() {
    super(BffAuthenticatedApiClient.getInstance());
  }

  getPayment(provider: PaymentProviderType, paymentId: string): Promise<HttpResponse<PaymentResponse>> {
    return this.apiClient.get<PaymentResponse>(
      BFF_ENDPOINTS.PAYMENT_BY_PROVIDER,
      {
        pathParams: {
          provider,
          paymentId,
        },
        retry: MAX_RETRY_ATTEMPTS,
        retryDelay: (context) => {
          // exponential backoff
          const attemptsRemaining = context.options.retry as number;
          const attempt = MAX_RETRY_ATTEMPTS - attemptsRemaining;
          return INITIAL_RETRY_DELAY_MS * Math.pow(RETRY_BACKOFF_BASE, Math.min(attempt, MAX_BACKOFF_EXPONENT));
        },
        retryStatusCodes: [HTTP_REQUEST_TIMEOUT],
      },
    );
  }

  /**
   * Create a payer profile
   *
   * @param request - Payer creation data
   * @param idempotencyKey - Unique key to prevent duplicate operations
   * @returns Promise<HttpResponse<PayerResponse>> - The created payer
   */
  createPayer(request: PayerData, idempotencyKey: string): Promise<HttpResponse<PayerResponse>> {
    return this.idempotencyManager.run(idempotencyKey, () =>
      this.apiClient.post<PayerResponse>(
        BFF_ENDPOINTS.PAYERS,
        {
          provider: request.provider,
          metadata: request.metadata,
        },
        {
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        },
      ));
  }

  /**
   * Create a payment intent for subscription
   * Payment intent does not uses idempotency manager as each intent should be unique
   *
   * @param request - Subscription payment creation data
   * @returns Promise<HttpResponse<PaymentIntentResponse>> - The created payment intent
   */
  createSubscriptionPaymentIntent(request: SubscriptionPaymentIntentData): Promise<HttpResponse<PaymentIntentResponse>> {
    return this.apiClient.post<PaymentIntentResponse>(
      BFF_ENDPOINTS.SUBSCRIPTIONS_PAYMENT_INTENT,
      {
        provider: request.provider,
        payer_id: request.payerId,
        redirect_uri: request.redirectUri,
        subscription: request.subscription,
        metadata: request.metadata,
        // Sent only when a campaign applies; omitted keeps the payload identical to plain checkout.
        ...(request.campaigns?.length ? { campaigns: request.campaigns } : {}),
      },
    );
  }

  /**
   * Create a payment intent for the change-card flow, to obtain the client secret
   * needed to tokenize a new card.
   *
   * @param request - Change-card payment intent data (amount, currency, etc.)
   * @returns Promise<HttpResponse<PaymentIntentResponse>> - The created payment intent
   */
  createChangeCardPaymentIntent(request: ChangeCardPaymentIntentData): Promise<HttpResponse<PaymentIntentResponse>> {
    return this.createMyPaymentIntent(request);
  }

  /**
   * Create a payment intent for the booking one-time-payment flow.
   *
   * @param request - Booking payment intent data (amount, currency, etc.)
   * @returns Promise<HttpResponse<PaymentIntentResponse>> - The created payment intent
   */
  createBookingPaymentIntent(request: BookingPaymentIntentData): Promise<HttpResponse<PaymentIntentResponse>> {
    return this.createMyPaymentIntent(request);
  }

  /*
   * Me-scoped intent shared by change-card and booking: the BFF resolves the payer from the caller's JWT, so it is not sent.
   */
  private createMyPaymentIntent(request: ChangeCardPaymentIntentData | BookingPaymentIntentData): Promise<HttpResponse<PaymentIntentResponse>> {
    return this.apiClient.post<PaymentIntentResponse>(
      BFF_ENDPOINTS.MY_PAYMENTS,
      {
        provider: request.provider,
        amount: request.amount,
        currency: request.currency,
        redirect_uri: request.redirectUri,
        metadata: request.metadata,
      },
    );
  }

  /**
   * Update the caller's stored payment method after a successful card tokenization.
   * The subscriber is resolved by the BFF from the caller's JWT.
   * Triggers a BFF workflow; poll WORKFLOW_STATUS until it reaches a terminal state.
   *
   * @param request - Change-payment-method payload (provider, recurrentToken, card details)
   * @returns Promise<HttpResponse<ChangePaymentMethodResponse>> - Workflow ID + initial status
   */
  changePaymentMethod(request: ChangePaymentMethodPayload): Promise<HttpResponse<ChangePaymentMethodResponse>> {
    return this.apiClient.post<ChangePaymentMethodResponse>(
      BFF_ENDPOINTS.MY_PAYMENT_METHOD,
      {
        provider: request.provider,
        recurrent_token: request.recurrentToken,
        payment_id: request.paymentId,
        metadata: request.metadata,
      },
    );
  }

  createSubscriptionAuthorization(paymentId: string, request: AuthorizationData): Promise<HttpResponse<AuthorizeResponse>> {
    return this.apiClient.post<AuthorizeResponse>(
      BFF_ENDPOINTS.AUTHORIZE,
      {
        provider: request.provider,
        amount: request.amount,
        currency: request.currency,
        payer_id: request.payerId,
        payment_id: request.paymentId,
        metadata: request.metadata,
      },
      {
        pathParams: {
          paymentId,
        },
      },
    );
  }

  /**
   * Confirm a payment intent
   *
   * @param paymentId - The payment ID to confirm
   * @param provider - Payment provider name
   * @param confirmationData - Payment confirmation data
   * @param idempotencyKey - Unique key to prevent duplicate operations
   * @returns Promise<HttpResponse<PaymentIntentResponse>> - The confirmed payment intent
   */
  confirmPaymentIntent(
    paymentId: string,
    provider: string,
    confirmationData: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<HttpResponse<PaymentIntentResponse>> {
    return this.idempotencyManager.run(idempotencyKey, () =>
      this.apiClient.post<PaymentIntentResponse>(
        BFF_ENDPOINTS.CONFIRM_PAYMENT_INTENT,
        {
          provider,
          confirmationData,
        },
        {
          pathParams: {
            paymentId,
          },
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        },
      ));
  }

  /**
   * Initialize tokenization for a payment provider
   * @param provider - Payment provider type (e.g., 'SPREEDLY')
   * @returns Promise with tokenization initialization data
   */
  initializeTokenization(provider: PaymentProviderType): Promise<HttpResponse<TokenizationInitResponse>> {
    return this.apiClient.post<TokenizationInitResponse>(
      BFF_ENDPOINTS.PAYMENT_TOKENIZATION_INIT,
      {},
      { pathParams: { provider } },
    );
  }

  /**
   * Open a 3-D Secure authentication session for a tokenized card (201).
   * The authentication provider travels in the body as the discriminator, not in the path.
   *
   * @param payload - Wire payload: { provider, payment_method: { provider, token }, currency, metadata }
   * @returns Promise<HttpResponse<PaymentAuthenticationResponse>> - PENDING session with the DDC iframe URL
   */
  createPaymentAuthentication(payload: CreatePaymentAuthenticationPayload): Promise<HttpResponse<PaymentAuthenticationResponse>> {
    return this.apiClient.post<PaymentAuthenticationResponse>(BFF_ENDPOINTS.PAYMENT_AUTHENTICATIONS, payload);
  }

  /**
   * Read one 3-D Secure authentication session. No ofetch retry: the caller polls.
   *
   * @param provider - Authentication provider (e.g. 'TRIDENT_CARDINAL')
   * @param authenticationId - The authentication session ID
   * @returns Promise<HttpResponse<PaymentAuthenticationResponse>> - Current session state
   */
  getPaymentAuthentication(provider: PaymentAuthenticationProviderType, authenticationId: string): Promise<HttpResponse<PaymentAuthenticationResponse>> {
    return this.apiClient.get<PaymentAuthenticationResponse>(
      BFF_ENDPOINTS.PAYMENT_AUTHENTICATION,
      { pathParams: { provider, authenticationId }, retry: false },
    );
  }

  /**
   * Get billing information
   * @returns Billing information response
   */
  // TODO: CVO-17996 / CVO-18290 update when BFF is ready. Move to BillingRepository
  getBillingInfo(): Promise<HttpResponse<BillingPaymentResponse>> {
    return this.apiClient.get<BillingPaymentResponse>(BFF_ENDPOINTS.BILLING_INFO);
  }
}

declare module '../Repositories' {
  export interface Repositories {
    [NAME]: PaymentRepository;
  }
}